import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '~/lib/api'

// Keys: ['matches', teamId, season, …] so the server's ['matches', teamId] invalidation and a
// team/season switch refetch exactly this data (ARCHITECTURE §6.2).
export const matchKeys = {
  all: ['matches'] as const,
  team: (teamId: string) => ['matches', teamId] as const,
  list: (teamId: string, season: string, scope: 'own' | 'league', matchday?: number) =>
    ['matches', teamId, season, 'list', scope, matchday ?? null] as const,
  hub: (teamId: string, season: string, id: string) => ['matches', teamId, season, 'hub', id] as const,
  plan: (teamId: string, season: string, id: string) => ['matches', teamId, season, 'plan', id] as const,
  recommendation: (teamId: string, season: string, id: string, formation: string) =>
    ['matches', teamId, season, 'recommendation', id, formation] as const,
  opponent: (teamId: string, season: string, id: string) => ['matches', teamId, season, 'opponent', id] as const,
  videos: (teamId: string, season: string, id: string) => ['matches', teamId, season, 'videos', id] as const,
  fixture: (teamId: string, season: string, id: string) => ['matches', teamId, season, 'fixture', id] as const,
  formations: ['matches', 'formations'] as const,
  blocks: (phase: string) => ['matches', 'tactic-blocks', phase] as const,
}



export const ownMatchesQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: matchKeys.list(teamId, season, 'own'),
    queryFn: async () => {
      const r = await unwrap(api.teams[':teamId'].matches.$get({ param: { teamId }, query: { season, scope: 'own' } }))
      return r.own!
    },
    enabled: !!teamId,
  })
export type OwnMatches = Awaited<ReturnType<NonNullable<ReturnType<typeof ownMatchesQuery>['queryFn']>>>
export type MatchItem = OwnMatches['upcoming'][number]

export const leagueMatchesQuery = (teamId: string, season: string) =>
  queryOptions({
    queryKey: matchKeys.list(teamId, season, 'league'),
    queryFn: async () => {
      const r = await unwrap(api.teams[':teamId'].matches.$get({ param: { teamId }, query: { season, scope: 'league' } }))
      return r.league!
    },
    enabled: !!teamId,
  })
export type LeagueMatches = Awaited<ReturnType<NonNullable<ReturnType<typeof leagueMatchesQuery>['queryFn']>>>
export type Matchday = LeagueMatches['matchdays'][number]
export type LeagueFixture = Matchday['fixtures'][number]

export const hubQuery = (teamId: string, season: string, id: string) =>
  queryOptions({
    queryKey: matchKeys.hub(teamId, season, id),
    queryFn: () => unwrap(api.matches[':id'].$get({ param: { id } })),
  })
export type Hub = Awaited<ReturnType<NonNullable<ReturnType<typeof hubQuery>['queryFn']>>>
export type HubEvent = Hub['events'][number]
export type HubLineupEntry = Hub['lineups']['home'][number]
export type PlanLineup = NonNullable<Hub['planLineup']>

export const fixtureQuery = (teamId: string, season: string, id: string) =>
  queryOptions({
    queryKey: matchKeys.fixture(teamId, season, id),
    queryFn: () => unwrap(api.fixtures[':id'].$get({ param: { id } })),
  })
export type FixtureDetail = Awaited<ReturnType<NonNullable<ReturnType<typeof fixtureQuery>['queryFn']>>>

export const reportPdfUrl = (id: string) => `/api/matches/${id}/report.pdf`

export const planQuery = (teamId: string, season: string, id: string) =>
  queryOptions({
    queryKey: matchKeys.plan(teamId, season, id),
    queryFn: () => unwrap(api.matches[':id'].plan.$get({ param: { id } })),
  })
export type PlanData = Awaited<ReturnType<NonNullable<ReturnType<typeof planQuery>['queryFn']>>>
export type Plan = PlanData['plan']
export type SquadPlayer = PlanData['squad'][number]

export const recommendationQuery = (teamId: string, season: string, id: string, formation: string) =>
  queryOptions({
    queryKey: matchKeys.recommendation(teamId, season, id, formation),
    queryFn: () => unwrap(api.matches[':id'].plan.recommendation.$get({ param: { id }, query: { formation } })),
    staleTime: 60_000,
  })
export type Recommendation = Awaited<ReturnType<NonNullable<ReturnType<typeof recommendationQuery>['queryFn']>>>
export type Candidate = Recommendation['candidates'][number]

export const formationsQuery = queryOptions({
  queryKey: matchKeys.formations,
  queryFn: () => unwrap(api.formations.$get()),
  staleTime: Infinity,
  gcTime: Infinity,
})
export type Formation = Awaited<ReturnType<NonNullable<(typeof formationsQuery)['queryFn']>>>[number]
export type FormationSlot = Formation['slots'][number]

export const opponentQuery = (teamId: string, season: string, id: string) =>
  queryOptions({
    queryKey: matchKeys.opponent(teamId, season, id),
    queryFn: () => unwrap(api.matches[':id'].opponent.$get({ param: { id } })),
  })
export type OpponentData = Awaited<ReturnType<NonNullable<ReturnType<typeof opponentQuery>['queryFn']>>>
export type Dossier = NonNullable<OpponentData['dossier']>

export const matchVideosQuery = (teamId: string, season: string, id: string) =>
  queryOptions({
    queryKey: matchKeys.videos(teamId, season, id),
    queryFn: () => unwrap(api.matches[':id'].videos.$get({ param: { id } })),
  })
export type MatchVideos = Awaited<ReturnType<NonNullable<ReturnType<typeof matchVideosQuery>['queryFn']>>>
export type Clip = MatchVideos['clips'][number]

export type Phase = 'in_possession' | 'pressing' | 'out_of_possession' | 'transition'

export const blocksQuery = (phase: Phase) =>
  queryOptions({
    queryKey: matchKeys.blocks(phase),
    queryFn: () => unwrap(api['tactic-blocks'].$get({ query: { phase } })),
  })
export type TacticBlock = Awaited<ReturnType<NonNullable<ReturnType<typeof blocksQuery>['queryFn']>>>[number]

// ---------------------------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------------------------

export type PlanPatch = Parameters<(typeof api.matches)[':id']['plan']['$put']>[0]['json']

export const savePlan = (id: string, json: PlanPatch) => unwrap(api.matches[':id'].plan.$put({ param: { id }, json }))

export function usePublishPlan(teamId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.matches[':id'].plan.publish.$post({ param: { id } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: matchKeys.team(teamId) }),
  })
}

export type MatchInput = Parameters<(typeof api.teams)[':teamId']['matches']['$post']>[0]['json']

export function useCreateMatch(teamId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (json: MatchInput) => unwrap(api.teams[':teamId'].matches.$post({ param: { teamId }, json })),
    onSuccess: () => qc.invalidateQueries({ queryKey: matchKeys.team(teamId) }),
  })
}

export function useUpdateMatch(teamId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, json }: { id: string; json: Partial<MatchInput> }) => unwrap(api.matches[':id'].$patch({ param: { id }, json })),
    onSuccess: () => qc.invalidateQueries({ queryKey: matchKeys.team(teamId) }),
  })
}

export function useDeleteMatch(teamId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => unwrap(api.matches[':id'].$delete({ param: { id } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: matchKeys.team(teamId) }),
  })
}

export const restoreMatch = (id: string) => unwrap(api.matches[':id'].restore.$post({ param: { id } }))

export type ResultInput = Parameters<(typeof api.matches)[':id']['result']['$patch']>[0]['json']

export function useSaveResult(teamId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, json }: { id: string; json: ResultInput }) => unwrap(api.matches[':id'].result.$patch({ param: { id }, json })),
    onSuccess: () => qc.invalidateQueries({ queryKey: matchKeys.team(teamId) }),
  })
}

export type BlockInput = Parameters<(typeof api)['tactic-blocks']['$post']>[0]['json']

export function useBlockMutations() {
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: ['matches', 'tactic-blocks'] })
  return {
    create: useMutation({ mutationFn: (json: BlockInput) => unwrap(api['tactic-blocks'].$post({ json })), onSuccess: refresh }),
    update: useMutation({
      mutationFn: ({ id, json }: { id: string; json: Partial<BlockInput> }) => unwrap(api['tactic-blocks'][':id'].$patch({ param: { id }, json })),
      onSuccess: refresh,
    }),
    remove: useMutation({ mutationFn: (id: string) => unwrap(api['tactic-blocks'][':id'].$delete({ param: { id } })), onSuccess: refresh }),
    restore: useMutation({ mutationFn: (id: string) => unwrap(api['tactic-blocks'][':id'].restore.$post({ param: { id } })), onSuccess: refresh }),
  }
}

export async function playbackUrl(recordingId: string, clipId?: string) {
  return unwrap(api.videos[':id'].playback.$get({ param: { id: recordingId }, query: clipId ? { clipId } : {} }))
}

export { loaderScope } from '~/lib/session'
